// The session: who is signed in (GET /api/me, still the v1 shape until WP access
// switches it to the v3 Me), sign out, switch organisation. Foundation-owned.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Me, SwitchOrgBody } from "@convenor/shared";
import { api, ApiError } from "./client";
import { qk } from "./keys";

/** The signed-in person. A 401 is an answer here ("signed out"), not an error to retry. */
export function useMe({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: qk.me,
    queryFn: () => api.get<Me>("/api/me"),
    retry: false,
    staleTime: 60_000,
    enabled,
  });
}

export const isSignedOutError = (e: unknown) => e instanceof ApiError && e.status === 401;

/** After any call that returns Me and changes who/which org: start from a clean cache. */
export function useSetMe() {
  const qc = useQueryClient();
  return (me: Me) => {
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
    qc.setQueryData(qk.me, me);
  };
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>("/api/auth/logout"),
    onSettled: () => {
      qc.clear();
    },
  });
}

export function useSwitchOrg() {
  const setMe = useSetMe();
  return useMutation({
    mutationFn: (body: SwitchOrgBody) => api.post<Me>("/api/me/switch-org", body),
    onSuccess: setMe,
  });
}
